import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, Clock, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";

const IMG = "https://images.unsplash.com/photo-1577412647305-991150c7d163?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxvZmZpY2VfMWNvbW1lcmNpYWx8&q=85";
const IMG_2 = "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxvZmZpY2VfMmNvbW1lcmNpYWx8&q=85";

const PROPERTY_TYPES = [
  { title: "Offices & Commercial Buildings", text: "Recurring cleaning that keeps workspaces presentable, healthy, and ready for clients." },
  { title: "Medical & Dental Offices", text: "Careful, consistent cleaning for waiting rooms, offices, and common areas." },
  { title: "Salons & Spas", text: "A spotless, calm environment your clients notice the moment they walk in." },
  { title: "Preschools & Daycares", text: "Thorough cleaning of high-touch surfaces, floors, and shared spaces." },
  { title: "Retail Spaces", text: "Floors, fitting rooms, restrooms, and storefronts that stay customer-ready." },
];

const SCHEDULES = ["Daily", "Weekly", "Bi-weekly", "Monthly", "After-hours & weekends", "One-time deep cleans"];

export default function Commercial() {
  const [svc, setSvc] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setSvc(r.data.find((s) => s.category === "commercial") || null)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Commercial Cleaning & Janitorial Services | St. Louis & Chesterfield, MO | Glow Green"
        description="Commercial cleaning in St. Louis and Chesterfield, MO: offices, medical and dental offices, salons, daycares, retail, recurring janitorial, restrooms, and floor care. Free quotes."
      />

      <section className="bg-forest grain relative pt-32 lg:pt-44 pb-20 lg:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="uppercase text-xs font-semibold tracking-[0.25em] text-leaf font-mono">Commercial Cleaning</p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              <MaskedLine delay={0.2}>A workplace that</MaskedLine>
              <MaskedLine delay={0.35} className="text-leaf">works as good as it looks.</MaskedLine>
            </h1>
            <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
              Recurring janitorial and detail cleaning for offices, retail, and professional spaces across St. Louis and Chesterfield - built around your hours, not ours.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="commercial-quote-button" className="inline-flex items-center gap-2 rounded-full bg-leaf px-7 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
                Get a Commercial Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl clipped-frame aspect-[4/3]">
              <img src={IMG} alt="Spotless open plan corporate office with desks and glass partitions" className="w-full h-full object-cover" fetchPriority="high" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">Who we serve</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-forest max-w-2xl">Built for business spaces of every kind.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROPERTY_TYPES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="rounded-2xl bg-white border border-forest/10 p-6 h-full hover:border-leaf/40 hover:shadow-lg transition-all">
                  <Building2 className="w-5 h-5 text-leaf" />
                  <h3 className="mt-3 font-display text-lg font-bold text-forest">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="rounded-2xl bg-forest text-white p-6 h-full grain relative overflow-hidden">
                <div className="relative z-10">
                  <Clock className="w-5 h-5 text-leaf" />
                  <h3 className="mt-3 font-display text-lg font-bold">Recurring schedules</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-white/75">
                    {SCHEDULES.map((s) => <li key={s} className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-leaf" />{s}</li>)}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-2xl clipped-frame aspect-[4/3]">
              <img src={IMG_2} alt="Modern clean commercial office interior under warm linear lighting" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf-hover">What's included</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-forest">Every visit, itemized.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Your quote spells out exactly what's covered on every visit - so there are no surprises and no hidden fees. Typical commercial scopes include:
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {(svc?.items || []).map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-leaf flex-shrink-0 mt-0.5" />{item}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-start gap-3 rounded-xl bg-mint/50 border border-leaf/20 p-4">
              <ShieldCheck className="w-5 h-5 text-leaf-hover flex-shrink-0 mt-0.5" />
              <p className="text-sm text-forest/80 leading-relaxed">
                Every commercial quote is custom: we walk your space, agree the scope in writing, and price it fairly - typically below comparable local quotes.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-forest grain relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center relative z-10">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Ready for a cleaner workplace?</h2>
          <p className="mt-4 text-white/70">Tell us about your space and we'll build a tailored estimate.</p>
          <Link to="/contact" data-testid="commercial-bottom-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
            Request a Free Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
